import produce from "immer";
import {
  GET_ALL_LOOK_AHEAD_COLUMN_CUSTOMIZATIONS_ERROR,
  GET_ALL_LOOK_AHEAD_COLUMN_CUSTOMIZATIONS_REQUEST,
  GET_ALL_LOOK_AHEAD_COLUMN_CUSTOMIZATIONS_SUCCESS,
  GET_LOOK_AHEAD_COLUMN_VISIBILITY_ERROR,
  GET_LOOK_AHEAD_COLUMN_VISIBILITY_REQUEST,
  GET_LOOK_AHEAD_COLUMN_VISIBILITY_SUCCESS,
  UPDATE_LOOK_AHEAD_COLUMN_CUSTOMIZATION_ERROR,
  UPDATE_LOOK_AHEAD_COLUMN_CUSTOMIZATION_REQUEST,
  UPDATE_LOOK_AHEAD_COLUMN_CUSTOMIZATION_SUCCESS,
  ADD_LOOK_AHEAD_CUSTOM_COLUMN_REQUEST,
  ADD_LOOK_AHEAD_CUSTOM_COLUMN_SUCCESS,
  ADD_LOOK_AHEAD_CUSTOM_COLUMN_ERROR,
  DELETE_LOOK_AHEAD_CUSTOM_COLUMN_REQUEST,
  DELETE_LOOK_AHEAD_CUSTOM_COLUMN_SUCCESS,
  DELETE_LOOK_AHEAD_CUSTOM_COLUMN_ERROR,
  UPDATE_LOOK_AHEAD_COLUMN_LOCK_REQUEST,
  UPDATE_LOOK_AHEAD_COLUMN_LOCK_SUCCESS,
  UPDATE_LOOK_AHEAD_COLUMN_LOCK_ERROR,
  UPDATE_LOOK_AHEAD_COLUMN_TYPE_REQUEST,
  UPDATE_LOOK_AHEAD_COLUMN_TYPE_SUCCESS,
  UPDATE_LOOK_AHEAD_COLUMN_TYPE_ERROR,
  UPDATE_LOOK_AHEAD_COLUMN_VISIBILITY_REQUEST,
  UPDATE_LOOK_AHEAD_COLUMN_VISIBILITY_SUCCESS,
  UPDATE_LOOK_AHEAD_COLUMN_VISIBILITY_ERROR,
  RENAME_LOOK_AHEAD_CUSTOM_COLUMN_REQUEST,
  RENAME_LOOK_AHEAD_CUSTOM_COLUMN_SUCCESS,
  RENAME_LOOK_AHEAD_CUSTOM_COLUMN_ERROR,
  UPDATE_GRID_LAYOUT_REQUEST,
  UPDATE_GRID_LAYOUT_SUCCESS,
  UPDATE_GRID_LAYOUT_ERROR,
} from "../types";

const initialState = {
  lookAhead: {
    isFetching: false,
    items: [],
    columnsVisibleOnTooltip: [],
    error: null,
    isUpdating: false,
    lastAdminUpdate: null,
  },
};

const PROPRETIES_TO_UPDATE = [
  "visible",
  // My Look Ahead layout fields (V1_57_24). Tooltip + task-creation
  // flags continue to flow through `updateLookAheadColumnVisibility`
  // which targets its own reducer cases.
  "visibleOnBacklog",
  "visibleOnTaskFocus",
  "backlogOrder",
  "taskFocusOrder",
];

export default function (state = initialState, action) {
  switch (action.type) {
    case GET_ALL_LOOK_AHEAD_COLUMN_CUSTOMIZATIONS_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isFetching = true;
        if (draftState.lookAhead.items?.length) draftState.lookAhead.items = [];
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case GET_ALL_LOOK_AHEAD_COLUMN_CUSTOMIZATIONS_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.items = action.payload.map((item, idx) => ({
          ...item,
          backlogOrder: item.backlogOrder !== undefined && item.backlogOrder !== null ? item.backlogOrder : idx,
          taskFocusOrder: item.taskFocusOrder !== undefined && item.taskFocusOrder !== null ? item.taskFocusOrder : idx,
          visibleOnBacklog: item.visibleOnBacklog !== false,
          visibleOnTaskFocus: item.visibleOnTaskFocus !== false,
        }));
        // Portfolio's admin layout-update time (epoch millis), shared by every
        // row. Surfacing it here means EVERY user — not just the admin — can
        // detect that the admin changed the default and reset a stale preset.
        const adminTs = action.payload.reduce(
          (max, item) => Math.max(max, Number(item.layoutUpdatedAt) || 0),
          0
        );
        draftState.lookAhead.lastAdminUpdate = adminTs || null;
        draftState.lookAhead.isFetching = false;
      });
    case GET_ALL_LOOK_AHEAD_COLUMN_CUSTOMIZATIONS_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isFetching = false;
        draftState.lookAhead.error = action.payload;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_CUSTOMIZATION_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_CUSTOMIZATION_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        if (Array.isArray(action.payload)) {
          // creating a map for each record to update. Carry the whole element
          // so every PROPRETIES_TO_UPDATE field (visibility + order) merges, not
          // just `visible`. The visibility-only popup payloads simply omit the
          // order keys, so the `property in updateObject` guard below skips them.
          const updateMap = action.payload.reduce((map, element) => {
            map[element.id] = element;
            return map;
          }, {});
          const newItems = draftState.lookAhead.items.slice();
          newItems.forEach((item) => {
            if (item.id in updateMap) {
              const updateObject = updateMap[item.id];
              // updating each property to update based on the created map
              PROPRETIES_TO_UPDATE.forEach((property) => {
                if (property in updateObject)
                  item[property] = updateObject[property];
              });
            }
          });
          draftState.lookAhead.items = newItems;
        } else
          console.error(
            "The payload passed down on update should be an array."
          );
      });
    case UPDATE_LOOK_AHEAD_COLUMN_CUSTOMIZATION_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case GET_LOOK_AHEAD_COLUMN_VISIBILITY_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isFetching = true;
        const oldColumnsVisibility =
          draftState.lookAhead.columnsVisibleOnTooltip;
        if (Array.isArray(oldColumnsVisibility) && oldColumnsVisibility.length)
          draftState.lookAhead.columnsVisibleOnTooltip = [];
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case GET_LOOK_AHEAD_COLUMN_VISIBILITY_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isFetching = false;
        draftState.lookAhead.columnsVisibleOnTooltip = action.payload;
      });
    case GET_LOOK_AHEAD_COLUMN_VISIBILITY_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isFetching = false;
        draftState.lookAhead.error = action.payload;
      });
    case ADD_LOOK_AHEAD_CUSTOM_COLUMN_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case ADD_LOOK_AHEAD_CUSTOM_COLUMN_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
      });
    case ADD_LOOK_AHEAD_CUSTOM_COLUMN_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case DELETE_LOOK_AHEAD_CUSTOM_COLUMN_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case DELETE_LOOK_AHEAD_CUSTOM_COLUMN_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.items = draftState.lookAhead.items.filter(
          (item) => item.id !== action.payload
        );
        draftState.lookAhead.columnsVisibleOnTooltip = (
          draftState.lookAhead.columnsVisibleOnTooltip || []
        ).filter((column) => column.id !== action.payload);
      });
    case DELETE_LOOK_AHEAD_CUSTOM_COLUMN_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_LOCK_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_LOCK_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        const { id, nameLocked } = action.payload;
        const item = draftState.lookAhead.items.find((i) => i.id === id);
        if (item) item.nameLocked = nameLocked;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_LOCK_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_TYPE_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_TYPE_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        const { id, columnType, dropdownValues } = action.payload;
        const item = draftState.lookAhead.items.find((i) => i.id === id);
        if (item) {
          item.columnType = columnType;
          item.dropdownValues = dropdownValues;
        }
      });
    case UPDATE_LOOK_AHEAD_COLUMN_TYPE_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_VISIBILITY_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case UPDATE_LOOK_AHEAD_COLUMN_VISIBILITY_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        if (Array.isArray(action.payload)) {
          const updateMap = action.payload.reduce((map, element) => {
            map[element.id] = {
              visibleOnTooltip: element.visibleOnTooltip,
              visibleOnTaskCreation: element.visibleOnTaskCreation,
            };
            return map;
          }, {});
          draftState.lookAhead.items.forEach((item) => {
            if (item.id in updateMap) {
              const update = updateMap[item.id];
              item.visibleOnTooltip = update.visibleOnTooltip;
              item.visibleOnTaskCreation = update.visibleOnTaskCreation;
            }
          });
        }
      });
    case UPDATE_LOOK_AHEAD_COLUMN_VISIBILITY_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case RENAME_LOOK_AHEAD_CUSTOM_COLUMN_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case RENAME_LOOK_AHEAD_CUSTOM_COLUMN_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        const { id, name } = action.payload;
        const item = draftState.lookAhead.items.find((i) => i.id === id);
        if (item) item.name = name;
      });
    case RENAME_LOOK_AHEAD_CUSTOM_COLUMN_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    case UPDATE_GRID_LAYOUT_REQUEST:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = true;
        if (draftState.lookAhead.error) draftState.lookAhead.error = null;
      });
    case UPDATE_GRID_LAYOUT_SUCCESS:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.lastAdminUpdate = action.payload.lastAdminUpdate;
        if (Array.isArray(action.payload.columns)) {
          const updateMap = action.payload.columns.reduce((map, element) => {
            map[element.id] = {
              backlogOrder: element.backlogOrder,
              taskFocusOrder: element.taskFocusOrder,
              visibleOnBacklog: element.visibleOnBacklog,
              visibleOnTaskFocus: element.visibleOnTaskFocus,
            };
            return map;
          }, {});
          draftState.lookAhead.items = draftState.lookAhead.items.map((item) => {
            if (item.id in updateMap) return { ...item, ...updateMap[item.id] };
            return item;
          });
        }
      });
    case UPDATE_GRID_LAYOUT_ERROR:
      return produce(state, (draftState) => {
        draftState.lookAhead.isUpdating = false;
        draftState.lookAhead.error = action.payload;
      });
    default:
      return state;
  }
}
